import axios from 'axios';
import store from '../store';
import moment from 'moment';
import { CurrentUserService } from './storage.service';
import { wait } from '../utils/index';
import { TOKEN_EXPIRE_DATE, STORAGE_KEY_TOKEN } from '@/config/consts';

/*
Make a base service which will do all the interaction with the network so we can easily
change or upgrade stuff in the future. This is exactly what we’re trying to achieve
with the api.service.js— encapsulate the Axios library so that when inevitably a new
thing comes along we can return to this single service and upgrade it without having to
refactor the whole application. Any other service that needs to interact with API will
simply import the ApiService and issue requests via the helper methods we’ve implemented.

Метод init необходимо использовать в main.js.
*/

export default {
    /// Stores the 401 interceptor position so that it can be later ejected when needed
    _interceptor: null,
    requestInterceptor: null,
    requestList: [],

    init(baseURL) {
        axios.defaults.baseURL = baseURL;
        axios.defaults.withCredentials = true;
        axios.defaults.showLoader = true;
        this.setAccessToken();
        this.setUserLogin();
    },

    setAccessToken() {
        axios.defaults.headers.common['Authorization'] = `Bearer ${CurrentUserService.getToken()}`;
    },
    setUserLogin() {
        axios.defaults.headers.common['X-ALSECO-User'] = CurrentUserService.getLogin();
    },

    async checkToken(config) {
        const tokenExpires = localStorage.getItem(TOKEN_EXPIRE_DATE);
        const localToken = localStorage.getItem(STORAGE_KEY_TOKEN);
        if (!tokenExpires) return;

        const isValid = moment(tokenExpires).isAfter(moment().format());
        const noneRequiredPaths = ['auth/signin', 'auth/refreshToken', 'auth/restorePassword', 'auth/updatePassword'];

        if (localToken && !isValid && !noneRequiredPaths.includes(config.url)) {
            this.requestList.push(config);

            if (this.requestList.length > 1) {
                await wait(1500);
                config.headers.common.Authorization = `Bearer ${store.getters['auth/loggedIn']}`;
                this.requestList = [];
                return true;
            }
            const token = await store.dispatch('auth/refreshToken');
            config.headers.common.Authorization = `Bearer ${token}`;
        }
        return true;
    },

    removeHeader() {
        axios.defaults.headers.common = {};
    },

    get(resource, options) {
        if (options) {
            return axios.get(resource, options);
        }
        return axios.get(resource);
    },

    post(resource, data, options) {
        if (options) {
            return axios.post(resource, data, options);
        } else {
            return axios.post(resource, data);
        }
    },

    put(resource, data) {
        return axios.put(resource, data);
    },

    delete(resource) {
        return axios.delete(resource);
    },

    /**
     * Perform a custom Axios request.
     *
     * data is an object containing the following properties:
     *  - method
     *  - url
     *  - data ... request payload
     *  - auth (optional)
     *    - username
     *    - password
     **/
    customRequest(data) {
        return axios(data);
    },

    enableRequestInterceptor() {
        this.requestInterceptor = axios.interceptors.request.use(
            async config => {
                this.setAccessToken();
                await this.checkToken(config);
                if (config.showLoader) {
                    await store.dispatch('loader/pending');
                }

                return config;
            },
            async function(error) {
                if (error.config.showLoader) {
                    await store.dispatch('loader/done');
                }
                return Promise.reject(error);
            }
        );
    },

    mountInterceptor(errorCallback) {
        this.enableRequestInterceptor();

        this._interceptor = axios.interceptors.response.use(
            async response => {
                if (store.state.health.isSystemInMaintenance) {
                    store.commit('health/setSystemInMaintenance', false);
                    await store.dispatch('permissions/getUserPermissions', null, { root: true });
                    await store.dispatch('users/getServOrg', null, { root: true });
                    await store.dispatch('base/initNavigationItems');
                }
                if (response.config.showLoader) {
                    await store.dispatch('loader/done');
                }
                return response;
            },
            async error => {
                let response = error.response;

                if (response.config.showLoader) {
                    await store.dispatch('loader/done');
                }
                errorCallback(error);
            }
        );
    },

    unmountInterceptor() {
        // Eject the interceptor
        axios.interceptors.response.eject(this._interceptor);
    }
};
